import * as React from 'react';
import { ReactSortable } from 'react-sortablejs';
import { Button } from '../Button/Button';
import { DatePicker as SitDatePicker } from '../DatePicker/DatePicker';
import { INPUT_MASK_VALIDATION } from '../../consts/date';
import moment from 'moment';
import {
  ConfirmationRequestTemplate,
  ConfirmationRequestTemplateField
} from '../../models/confirmationRequestTemplate';
import { INVITE_KIND } from 'Src/consts/invite';
import { INVITE_KIND_TYPE } from 'Src/types/invite';
import './ConfirmationRequestForm.css';
import { FormDefinition } from '../../types/formSchema';
import { formDefinitionToLegacyFields, legacyFieldsToFormDefinition } from '../../helpers/form/legacyFormAdapter';
import { SendTimeFormEditor } from '../SendTimeFormEditor/SendTimeFormEditor';
import { stripUnsignedFormMediaKeys, uploadFormFile, uploadFormImage } from '../../helpers/form/formMedia';
import { AppUser } from '../../views/App/App';
import { validateFormDefinitionStructure } from '../../helpers/form/formDefinitionValidation';

interface Props {
  user: AppUser;
  template?: ConfirmationRequestTemplate;
  onCancelClick: () => void;
  onSendConfirmationRequestsClick: (
    fields: ConfirmationRequestFormField[],
    deadlineForAnswers?: string,
    formDefinition?: FormDefinition
  ) => void;
  textSubmitButton?: string;
  isRenderDeadline?: boolean;
  inviteKind?: INVITE_KIND_TYPE;
  initialSelectedFields?: ConfirmationRequestTemplateField[];
  templateType?: 'CLUB_BOOKING' | 'TOURNAMENT_CONFIRMATION';
  defaultBuilderMode?: 'COMPACT' | 'FULL';
  customClassName?: string;
}

interface State {
  fields: ConfirmationRequestFormField[];
  formDefinition: FormDefinition;
  deadlineForAnswers: Date | null;
  deadlineForAnswersRaw: string;
  isDeadlineForAnswersError: boolean;
}

export interface ConfirmationRequestFormField extends ConfirmationRequestTemplateField {
  isChecked: boolean;
  id: number;
}

export class ConfirmationRequestForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    const template = props.template;
    const templateFields: ConfirmationRequestTemplateField[] = Array.isArray(template?.fields)
      ? [...template.fields]
      : [];
    const isV2Template = template?.schemaVersion === 2 || Boolean(template?.formDefinition);

    const initialSelected: ConfirmationRequestTemplateField[] = Array.isArray(props.initialSelectedFields)
      ? props.initialSelectedFields
      : [];

    const keyOf = (f: any) => String((f && (f._id || f.id || f.heading)) || '');

    const selectedKeys = new Set(initialSelected.map(keyOf).filter(Boolean));

    const orderedSelected = initialSelected
      .map(sf => templateFields.find(tf => keyOf(tf) === keyOf(sf)) || sf)
      .filter(Boolean);

    const orderedRest = templateFields.filter(tf => !selectedKeys.has(keyOf(tf)));

    const mergedOrdered = initialSelected.length > 0 ? [...orderedSelected, ...orderedRest] : templateFields;

    const fields = mergedOrdered.map((field: any, index: number) => {
      const isChecked =
        initialSelected.length > 0 ? selectedKeys.has(keyOf(field)) : isV2Template ? true : Boolean(field?.isDefault);

      return {
        ...field,
        isChecked,
        id: index
      };
    });

    this.state = {
      deadlineForAnswers: null,
      deadlineForAnswersRaw: '',
      isDeadlineForAnswersError: false,
      fields,
      formDefinition:
        isV2Template && template?.formDefinition
          ? template.formDefinition
          : legacyFieldsToFormDefinition(templateFields, {
              title: props.templateType === 'CLUB_BOOKING' ? 'Club booking form' : 'Request form'
            })
    };
  }

  onCheckboxClick = index => {
    const fields = this.state.fields;
    const nextFields = fields.map((field, fieldIndex) => {
      if (fieldIndex === index) {
        return {
          ...field,
          isChecked: !field.isChecked
        };
      } else {
        return field;
      }
    });

    this.setState({
      fields: nextFields
    });
  };

  sortFields = sortedFields => {
    this.setState({
      fields: sortedFields
    });
  };

  renderFields() {
    const { fields } = this.state;

    return (
      <>
        <h6 className="mb-2">Selected questions</h6>

        {fields.filter(field => field.isChecked).length === 0 ? (
          <div className="text-muted mb-3" style={{ fontSize: 13 }}>
            No selected questions yet.
          </div>
        ) : null}

        <ReactSortable
          list={fields.filter(field => field.isChecked)}
          setList={(sortedChecked: ConfirmationRequestFormField[]) => {
            const unchecked = this.state.fields.filter(field => !field.isChecked);
            this.sortFields([...sortedChecked, ...unchecked]);
          }}
          handle=".cpDrag"
          animation={150}
        >
          {fields
            .filter(field => field.isChecked)
            .map(field => (
              <label key={`confirmation_request_field_${field._id || field.id}`} className="cpFieldsManageRow">
                <input
                  type="checkbox"
                  checked
                  onChange={() => this.onCheckboxClick(this.state.fields.findIndex(f => f.id === field.id))}
                  style={{ marginTop: 4 }}
                />

                <div className="cpFieldsManageRowMain">
                  <div className="cpFieldsManageRowTitle">{field.heading}</div>

                  <div className="cpBadges mt-1" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span className="cpBadge cpBadge--info">{String(field.type || 'STRING')}</span>
                    <span className={`cpBadge ${field.isRequired ? 'cpBadge--warn' : 'cpBadge--muted'}`}>
                      {field.isRequired ? 'Required' : 'Optional'}
                    </span>
                    <span className={`cpBadge ${field.isDefault ? 'cpBadge--info' : 'cpBadge--muted'}`}>
                      {field.isDefault ? 'Default' : 'Extra'}
                    </span>
                    {Array.isArray((field as any).enumOptions) && (field as any).enumOptions.length > 0 ? (
                      <span className="cpBadge cpBadge--muted">{(field as any).enumOptions.length} options</span>
                    ) : null}
                  </div>
                </div>

                <div className="cpDrag cpFieldsManageRowDrag">⋮⋮</div>
              </label>
            ))}
        </ReactSortable>

        <h6 className="mt-4 mb-2">Available questions</h6>

        {fields.filter(field => !field.isChecked).length === 0 ? (
          <div className="text-muted mb-3" style={{ fontSize: 13 }}>
            No available questions.
          </div>
        ) : null}

        {fields
          .filter(field => !field.isChecked)
          .map(field => (
            <label key={`confirmation_request_field_available_${field._id || field.id}`} className="cpFieldsManageRow">
              <input
                type="checkbox"
                checked={false}
                onChange={() => this.onCheckboxClick(this.state.fields.findIndex(f => f.id === field.id))}
                style={{ marginTop: 4 }}
              />

              <div className="cpFieldsManageRowMain">
                <div className="cpFieldsManageRowTitle">{field.heading}</div>

                <div className="cpBadges mt-1" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <span className="cpBadge cpBadge--info">{String(field.type || 'STRING')}</span>
                  <span className={`cpBadge ${field.isRequired ? 'cpBadge--warn' : 'cpBadge--muted'}`}>
                    {field.isRequired ? 'Required' : 'Optional'}
                  </span>
                  <span className={`cpBadge ${field.isDefault ? 'cpBadge--info' : 'cpBadge--muted'}`}>
                    {field.isDefault ? 'Default' : 'Extra'}
                  </span>
                  {Array.isArray((field as any).enumOptions) && (field as any).enumOptions.length > 0 ? (
                    <span className="cpBadge cpBadge--muted">{(field as any).enumOptions.length} options</span>
                  ) : null}
                </div>
              </div>
            </label>
          ))}
      </>
    );
  }

  onSelectAllClick = () => {
    const { fields } = this.state;
    const isSelectAllChecked = fields.every(field => field.isChecked);

    this.setState({
      fields: fields.map(field => {
        if (isSelectAllChecked) {
          return {
            ...field,
            isChecked: false
          };
        } else {
          return {
            ...field,
            isChecked: true
          };
        }
      })
    });
  };

  renderSelectAll(): React.ReactNode {
    const { fields } = this.state;
    const isSelectAllChecked = fields.every(field => field.isChecked);

    return (
      <div className="bConsentRequestFields">
        <div className="pl-3">
          <div className={'custom-control custom-switch'} key={`confirmation_request_field_selectAll`}>
            <input
              id={`switch_selectAll`}
              onChange={this.onSelectAllClick}
              checked={isSelectAllChecked}
              type="checkbox"
              className="custom-control-input"
            />
            <label className="pl-3 mb-3 mt-3 custom-control-label" htmlFor={`switch_selectAll`}>
              {isSelectAllChecked ? 'Unselect All' : 'Select All'}
            </label>
          </div>
        </div>
      </div>
    );
  }

  onSendClick = () => {
    const { deadlineForAnswersRaw, formDefinition } = this.state;
    const sanitizedDefinition = stripUnsignedFormMediaKeys(formDefinition);
    const structureIssues = validateFormDefinitionStructure(sanitizedDefinition);
    if (structureIssues.length > 0) {
      return;
    }

    const cleanedFields = formDefinitionToLegacyFields(sanitizedDefinition);
    const raw = (deadlineForAnswersRaw || '').trim();

    if (!raw) {
      this.props.onSendConfirmationRequestsClick(cleanedFields as any, '', sanitizedDefinition);
      return;
    }

    const m = moment(raw, INPUT_MASK_VALIDATION, true);
    const isOk = m.isValid() && m.isAfter(moment());

    if (!isOk) {
      this.setState({ isDeadlineForAnswersError: true });
      return;
    }

    this.props.onSendConfirmationRequestsClick(cleanedFields as any, raw, sanitizedDefinition);
  };

  renderButtons(): React.ReactNode {
    const textSubmitButton = this.props.textSubmitButton || 'Send confirmation request';
    return (
      <>
        <Button onClick={this.props.onCancelClick} text={'Cancel'} customClass={'btn-secondary mt-3 mr-3'} />
        <Button onClick={this.onSendClick} text={textSubmitButton} customClass={'mt-3'} />
      </>
    );
  }

  setDeadlineFromDate = (date: Date | null) => {
    this.setState({
      deadlineForAnswers: date,
      deadlineForAnswersRaw: date ? moment(date).format(INPUT_MASK_VALIDATION) : '',
      isDeadlineForAnswersError: false
    });
  };

  onDeadlineChange = (date: Date | null) => {
    this.setDeadlineFromDate(date);
  };

  onDeadlineChangeRaw = (event: any) => {
    const raw = event?.target?.value || '';

    this.setState({ deadlineForAnswersRaw: raw });

    if (!raw) {
      this.setState({ deadlineForAnswers: null, isDeadlineForAnswersError: false });
      return;
    }

    const m = moment(raw, INPUT_MASK_VALIDATION, true);

    if (!m.isValid()) {
      this.setState({ isDeadlineForAnswersError: true });
      return;
    }

    this.setState({
      deadlineForAnswers: m.toDate(),
      isDeadlineForAnswersError: !m.isAfter(moment())
    });
  };

  renderManageHeader(): React.ReactNode {
    const { fields } = this.state;
    const selectedCount = fields.filter(field => field.isChecked).length;
    const totalCount = fields.length;
    const availableCount = Math.max(0, totalCount - selectedCount);

    return (
      <div className="cpFieldsManageHeader">
        <div className="cpFieldsManageHeaderMain">
          <div className="cpFieldsManageTitleRow">
            <div className="cpFieldsManageTitle">Select questions</div>

            <div className="cpBadges" style={{ marginTop: 0 }}>
              <span className="cpBadge cpBadge--info">Selected: {selectedCount}</span>
              <span className="cpBadge cpBadge--muted">Available: {availableCount}</span>
              <span className="cpBadge cpBadge--muted">Total: {totalCount}</span>
            </div>
          </div>

          <div className="cpFieldsManageSubtitle">
            Choose which questions schools will answer, then drag selected questions to reorder them.
          </div>
        </div>

        <div className="cpFieldsManageHeaderActions">
          <Button text="Select all" customClass="btn-outline-secondary" onClick={this.onSelectAllClick} />
        </div>
      </div>
    );
  }

  renderDeadline(): React.ReactNode {
    const { deadlineForAnswers } = this.state;

    return (
      <SitDatePicker
        mode="datetime"
        value={deadlineForAnswers}
        onChange={this.onDeadlineChange}
        placeholder="Select date and time"
        format="DD/MM/YYYY HH:mm"
        min={new Date()}
        minuteStep={5}
        clearable
        placement="top"
        align="right"
        className="confirmationRequestForm__dateTimePicker"
      />
    );
  }

  renderHeaderText() {
    const { inviteKind } = this.props;

    switch (inviteKind) {
      case INVITE_KIND.TOURNAMENT_SCHOOL_INVITE:
        return (
          <div className="mt-2">
            Here you can select questions that schools will be asked to answer when sending their participation
            confirmation. You can set up your questions on your union console.
          </div>
        );

      case INVITE_KIND.CLUB_SCHOOL_INVITE:
        return (
          <div className="mt-2">
            Here you can select questions that parents/students will be asked to answer when booking onto the club(s).
            You can set up your questions on your school console.
          </div>
        );

      default:
        return (
          <div className="mt-2">
            Here you can select questions that parents will be asked to answer when sending their parental consent. You
            can set up your questions on your school console.
          </div>
        );
    }
  }

  render() {
    const { formDefinition, isDeadlineForAnswersError } = this.state;
    const questionCount = formDefinition.elements.filter(element => element.kind === 'QUESTION').length;
    const structureIssues = validateFormDefinitionStructure(formDefinition);
    const { isRenderDeadline } = this.props;

    return (
      <div className={this.props.customClassName || undefined}>
        <SendTimeFormEditor
          definition={formDefinition}
          onChange={next => this.setState({ formDefinition: next })}
          uploadImage={file => uploadFormImage(this.props.user, file)}
          uploadFile={file => uploadFormFile(this.props.user, file)}
          user={this.props.templateType ? this.props.user : undefined}
          templateType={this.props.templateType}
          initialTemplateId={this.props.template?.id}
          defaultTemplateName={this.props.template?.name || 'Form template'}
          selectDefaultTemplateOnLoad={false}
          applyDefaultTemplateOnLoad={false}
          onCancelRequested={this.props.onCancelClick}
          onSendRequested={this.onSendClick}
          sendButtonText={this.props.textSubmitButton || 'Send'}
          defaultBuilderMode={this.props.defaultBuilderMode}
          showTemplateSaveAction={true}
          footerBeforeActions={
            <>
              {structureIssues.length > 0 && (
                <div className="confirmationRequestForm__error">
                  Fix the highlighted form configuration issues before sending.
                </div>
              )}
              {isRenderDeadline && (
                <div className="confirmationRequestForm__deadline">
                  <div>
                    <strong>Deadline for answers</strong>
                    <span>Optional. Leave empty when responses have no deadline.</span>
                  </div>
                  {this.renderDeadline()}
                </div>
              )}
              {isRenderDeadline && isDeadlineForAnswersError && (
                <div className="confirmationRequestForm__error">Please select a future date and time.</div>
              )}
              <div className="confirmationRequestForm__summary">{`${questionCount} questions will be answered`}</div>
            </>
          }
        />
      </div>
    );
  }
}
